import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "block text-sm font-bold text-black mb-2"
}
const _hoisted_2 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_3 = { class: "relative" }
const _hoisted_4 = ["placeholder", "onKeydown"]
const _hoisted_5 = {
  key: 1,
  class: "absolute z-30 mt-1 w-full max-h-72 overflow-auto bg-white border border-gray-300 rounded-lg shadow-lg"
}
const _hoisted_6 = {
  key: 0,
  class: "px-3 py-3 text-sm text-gray-500 text-center"
}
const _hoisted_7 = ["onMousedown", "onMouseenter"]
const _hoisted_8 = { class: "block truncate" }

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { CategorieNode, CategoryPathSelection } from "@/types/categorie";
import { focusRelativeElement } from "@/utils/focusNavigation";

export type { CategoryPathSelection };

type FlatCategoryOption = CategoryPathSelection & {
  searchText: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'CategoryTreeAutocomplete',
  props: {
    tree: {},
    catId: { default: "" },
    sousId: { default: "" },
    sousSousId: { default: "" },
    label: { default: "Catégorie" },
    placeholder: { default: "Rechercher une catégorie (ex. robe, jean…)" },
    required: { type: Boolean, default: true },
    maxResults: { default: 50 }
  },
  emits: ["select", "clear"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const searchTerm = ref("");
const isOpen = ref(false);
const highlightedIndex = ref(0);
const suppressSync = ref(false);

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const flattenLeaves = (
  nodes: CategorieNode[],
  pathLabels: string[] = [],
  pathIds: string[] = [],
): FlatCategoryOption[] => {
  const result: FlatCategoryOption[] = [];

  for (const node of nodes || []) {
    const labels = [...pathLabels, node.label];
    const ids = [...pathIds, String(node.id_categorie)];
    const children = node.children || [];

    if (children.length === 0) {
      const pathLabel = labels.join(" > ");
      const l = ids.length;
      // Aligne Mel / fiche produit : on retient au plus les 3 derniers niveaux
      const catId = l >= 3 ? ids[l - 3] : ids[0] || "";
      const sousId = l >= 3 ? ids[l - 2] : l >= 2 ? ids[1] : "";
      const sousSousId = l >= 3 ? ids[l - 1] : "";
      result.push({
        catId,
        sousId,
        sousSousId,
        leafId: ids[l - 1] || "",
        pathLabel,
        searchText: normalize(labels.join(" ")),
      });
    } else {
      result.push(...flattenLeaves(children, labels, ids));
    }
  }

  return result;
};

const allLeafOptions = computed(() => flattenLeaves(props.tree || []));

const resolvePathLabel = computed(() => {
  const leafId =
    props.sousSousId || props.sousId || props.catId || "";
  if (!leafId) return "";

  const match = allLeafOptions.value.find((o) => {
    if (props.sousSousId) {
      return (
        o.catId === props.catId &&
        o.sousId === props.sousId &&
        o.sousSousId === props.sousSousId
      );
    }
    if (props.sousId) {
      return (
        o.catId === props.catId &&
        o.sousId === props.sousId &&
        !o.sousSousId
      );
    }
    return o.catId === props.catId && !o.sousId && !o.sousSousId;
  });

  if (match) return match.pathLabel;

  // Fallback: any leaf under the selected path prefix
  const fallback = allLeafOptions.value.find((o) => o.leafId === leafId);
  return fallback?.pathLabel || "";
});

const hasSelection = computed(() => !!resolvePathLabel.value);

const filteredOptions = computed(() => {
  const term = normalize(searchTerm.value);
  const showAll =
    !term ||
    (hasSelection.value && searchTerm.value === resolvePathLabel.value);

  const scored = allLeafOptions.value
    .map((option) => {
      const idx = showAll ? 0 : option.searchText.indexOf(term);
      if (idx < 0) return null;
      const score = idx === 0 ? 0 : idx;
      return { option, score };
    })
    .filter((x): x is { option: FlatCategoryOption; score: number } => !!x)
    .sort(
      (a, b) =>
        a.score - b.score ||
        a.option.pathLabel.localeCompare(b.option.pathLabel, "fr"),
    );

  return scored.slice(0, props.maxResults).map((x) => x.option);
});

watch(filteredOptions, () => {
  highlightedIndex.value = 0;
});

watch(
  () => [props.catId, props.sousId, props.sousSousId, allLeafOptions.value.length] as const,
  async () => {
    if (suppressSync.value) return;
    const label = resolvePathLabel.value;
    if (label && !isOpen.value) {
      searchTerm.value = label;
    } else if (!props.catId && !props.sousId && !props.sousSousId && !isOpen.value) {
      searchTerm.value = "";
    }
  },
  { immediate: true },
);

const onFocus = () => {
  isOpen.value = true;
  if (hasSelection.value) {
    // Keep current label visible but allow typing over it
    nextTick(() => {
      inputRef.value?.select();
    });
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  // Restore selected label if user typed without selecting
  searchTerm.value = resolvePathLabel.value;
};

const moveHighlight = (delta: number) => {
  if (!isOpen.value) {
    isOpen.value = true;
    return;
  }
  const len = filteredOptions.value.length;
  if (!len) return;
  highlightedIndex.value = (highlightedIndex.value + delta + len) % len;
};

const confirmHighlight = () => {
  const option = filteredOptions.value[highlightedIndex.value];
  if (option) selectOption(option);
};

const handleTab = (event: KeyboardEvent) => {
  closeDropdown();
  void nextTick(() => {
    focusRelativeElement(inputRef.value, event.shiftKey ? -1 : 1);
  });
};

const selectOption = (option: FlatCategoryOption) => {
  suppressSync.value = true;
  searchTerm.value = option.pathLabel;
  isOpen.value = false;
  emit("select", {
    catId: option.catId,
    sousId: option.sousId,
    sousSousId: option.sousSousId,
    leafId: option.leafId,
    pathLabel: option.pathLabel,
  });
  nextTick(() => {
    suppressSync.value = false;
  });
};

const clearSelection = () => {
  searchTerm.value = "";
  isOpen.value = false;
  emit("clear");
  nextTick(() => {
    inputRef.value?.focus();
  });
};

const onDocumentClick = (event: MouseEvent) => {
  if (!rootRef.value) return;
  if (!rootRef.value.contains(event.target as Node)) {
    if (isOpen.value) closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener("mousedown", onDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener("mousedown", onDocumentClick);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "relative",
    ref_key: "rootRef",
    ref: rootRef
  }, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("label", _hoisted_1, [
          _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
          (_ctx.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _cache[5] || (_cache[5] = _createElementVNode("i", { class: "fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" }, null, -1)),
      _withDirectives(_createElementVNode("input", {
        ref_key: "inputRef",
        ref: inputRef,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
        type: "text",
        autocomplete: "off",
        placeholder: _ctx.placeholder,
        class: "w-full pl-9 pr-9 py-2 border rounded-lg bg-white text-sm text-black focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors border-gray-300",
        onFocus: onFocus,
        onInput: _cache[1] || (_cache[1] = ($event: any) => (isOpen.value = true)),
        onBlur: closeDropdown,
        onKeydown: [
          _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (moveHighlight(1)), ["prevent"]), ["down"])),
          _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event: any) => (moveHighlight(-1)), ["prevent"]), ["up"])),
          _withKeys(_withModifiers(confirmHighlight, ["prevent"]), ["enter"]),
          _withKeys(_withModifiers(closeDropdown, ["prevent"]), ["escape"]),
          _withKeys(_withModifiers(handleTab, ["prevent"]), ["tab"])
        ]
      }, null, 40, _hoisted_4), [
        [_vModelText, searchTerm.value]
      ]),
      (hasSelection.value || searchTerm.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            tabindex: "-1",
            class: "absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs p-1",
            title: "Effacer",
            onClick: clearSelection
          }, _cache[4] || (_cache[4] = [
            _createElementVNode("i", { class: "fas fa-times" }, null, -1)
          ])))
        : _createCommentVNode("", true)
    ]),
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (filteredOptions.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(searchTerm.value.trim()
            ? "Aucune catégorie trouvée"
            : "Tapez pour rechercher une catégorie"), 1))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (option, index) => {
            return (_openBlock(), _createElementBlock("button", {
              key: option.leafId,
              type: "button",
              tabindex: "-1",
              class: _normalizeClass(["w-full text-left px-3 py-2 text-sm text-black cursor-pointer border-b border-gray-50 last:border-b-0", 
          index === highlightedIndex.value
            ? 'bg-blue-50'
            : 'hover:bg-blue-50'
        ]),
              onMousedown: _withModifiers(($event: any) => (selectOption(option)), ["prevent"]),
              onMouseenter: ($event: any) => (highlightedIndex.value = index)
            }, [
              _createElementVNode("span", _hoisted_8, _toDisplayString(option.pathLabel), 1)
            ], 42, _hoisted_7))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ], 512))
}
}

})